import React, { ChangeEvent, useEffect, useReducer, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Tooltip,
  Alert
} from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef, GridRowModes, GridRowModesModel, GridRowId, GridRowModel } from '@mui/x-data-grid';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import environment from "../../environment/environment";
import { useRecoilState } from "recoil";
import { tokenState } from "../../atoms/token";
import { userState } from "../../atoms/user";
import { ConfirmationDialog } from "./confirmation-dialog";

interface UploadDialogProps {
  record: any;
  handleCloseClick: () => void;
  handleSaveFileEdit: (children: ChildAsset[], files: File[]) => void;
  delayUpload?: boolean;
}

interface ChildAsset {
  id: number | string;
  AssetID?: number;
  title: string;
  pdfName: string;
  pdfSize?: number;
  order?: number;
  isNew?: boolean;
  isUploading?: boolean;
}

export function UploadDialog({ record, handleCloseClick, handleSaveFileEdit, delayUpload }: UploadDialogProps) {
  const [children, setChildren] = useState<ChildAsset[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [token] = useRecoilState(tokenState);
  const [userInfo] = useRecoilState(userState);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [uploadError, setUploadError] = useState<string | null>(null); 

  const [form, dispatch] = useReducer(
    (state: Record<string, any>, updates: Record<string, any>) => ({ ...state, ...updates }),
    {}
  );

  // ----- Column Definitions -----
  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Title', width: 200, editable: true, 
      renderCell: (params) => (
      <span>
        {params.row.title}
        {params.row.isUploading
          ? ' (Uploading)'
          : params.row.isNew
            ? ' (New)'
            : ''}
      </span>
    )},
    { field: 'pdfName', headerName: 'File Name', width: 200 },
    { field: 'order', headerName: 'Order', width: 100, editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        return isEditing ? [
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={["fas", "save"]} />}
            label="Save"
            sx={{
              color: 'primary.main',
            }}
            onClick={onSaveRow(id)}
          />,
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={["fas", "cancel"]} />}
            label="Cancel"
            className="textPrimary"
            onClick={onCancelRow(id)}
            color="inherit"
          />
        ] : [
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={["fas", "edit"]} />}
            label="Edit"
            className="textPrimary"
            onClick={onEditRow(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={["fas", "trash"]} />}
            label="Delete"
            onClick={onDeleteRow(id)}
            color="inherit"
          />
        ];
      }
    }
  ];

  // ----- Effects -----
  useEffect(() => {
    const initialChildren = (record.children ?? record.parentStub?.children) || [];
    initialChildren.forEach((child: any, idx: number) => {
      child.id = child.AssetID ?? idx;
    });
    setChildren(initialChildren as ChildAsset[]);
    setLoading(false);
  }, [record]);

  // ----- Handlers -----
  const onDeleteAll = () => {
    setChildren([]);
    setFiles([]);
  };

  const onUploadClick = () => {
    inputRef.current?.click();
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected?.length) return;

    const updatedChildren: ChildAsset[] = [...children];
    const updatedFiles: File[] = [...files];

    Array.from(selected).forEach(file => {
      updatedChildren.push({
        id: file.name,
        title: file.name,
        pdfName: file.name,
        pdfSize: file.size,
        isNew: true,
        isUploading: false
      });
      updatedFiles.push(file);
    });

    setChildren(updatedChildren);
    setFiles(updatedFiles);
    dispatch({ UserId: userInfo.userId });
  };

  // Upload files and then invoke parent save
  const onSaveClick = async () => {    
    
    setUploadError(null); 
    // If there are files, upload first
    if (files.length) {
      //Inform the user of uploading
      setChildren(children =>
        children.map(c =>
          c.isNew
            ? { ...c, isUploading: true }
            : c
        )
      );

      setLoading(true);

      // build FormData as before
      const formData = new FormData();
      files.forEach(f => formData.append('Files', f));
      formData.append('Record', JSON.stringify({ Children: children }));

      try {
        const res = await axios.post(
          `${environment.eticApiUrl}updateRecord/${record.ticParentDocId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }}
        );

        //Set Status 
        const updated = res.data.children.map((c: any) => ({
          ...c,
          isUploading: false,
          isNew: false
        }));

        setChildren(updated);
        setFiles([]);
        handleSaveFileEdit(updated, []);
        handleCloseClick();
      } 
      catch (err) 
      {
        setUploadError("File upload failed. Please try again."); 

        setChildren(children =>
        children.map(c =>
          c.isUploading
            ? { ...c, isUploading: false }
            : c
        )
      );

      } finally {
        setLoading(false);
      }
    } else {
      // no files to upload, just notify parent and close immediately
      handleSaveFileEdit(children, files);
      handleCloseClick();
    }
  };

  const onEditRow = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const onSaveRow = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const onCancelRow = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
    setChildren(children.filter(row => !(row.id === id && row.isNew)));
  };

  const onDeleteRow = (id: GridRowId) => () => {
    setChildren(children.filter(row => row.id !== id));
  };

  const onRowUpdate = (newRow: GridRowModel) => {
    const existing = children.find(row => row.id === newRow.id)!;
    const updatedRow: ChildAsset = { ...existing, ...newRow, isNew: false };
    setChildren(children.map(row => row.id === newRow.id ? updatedRow : row));
    return updatedRow;
  };

  const onRowModesChange = (model: GridRowModesModel) => {
    setRowModesModel(model);
  };

  const handleConfirmationClose = (confirmed: boolean) => {
    setShowConfirmation(false);
    if (confirmed) onDeleteAll();
  };

  // ----- Render -----
  const isSaveDisabled = loading;
  const saveTooltip = isSaveDisabled
    ? "Please wait for files to finish uploading before saving."
    : "";

  return (
    <Dialog open PaperProps={{ style: { minWidth: '700px' } }}>
      <DialogTitle sx={{ fontWeight: "bolder" }}>Edit File(s)</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: "rgba(63, 66, 66, 0.76)", whiteSpace: "pre-line" }}>
          <Stack direction="row" spacing={1} mb={2}>
            <Button 
            variant="contained" 
            startIcon={<FontAwesomeIcon icon={["fas","file-upload"]}/>} 
            onClick={onUploadClick}>
              New Upload
            </Button>
            {loading && <CircularProgress size={24} />}
            <input 
              type="file" 
              ref={inputRef} 
              onChange={onFileChange} 
              hidden 
              multiple />
          </Stack>

          <h2>Current Files</h2>

          <Stack direction="row" spacing={1} mb={2}>
            <Button 
              variant="contained"
              startIcon={<FontAwesomeIcon icon={["fas","trash"]}/>} 
              onClick={() => setShowConfirmation(true)}>
                Delete All
            </Button>
            {showConfirmation && (
              <ConfirmationDialog
                title="Delete Asset"
                dialogText="Are you sure you want to remove all files?"
                closeButtonText="No"
                handleActionButtonText="Yes"
                onChangeEvent={handleConfirmationClose}
              />
            )}
          </Stack>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <div style={{ height: 400, width: '100%' }}>
            <DataGrid
              getRowHeight={(params) => 'auto'}
              sx={{ '.MuiDataGrid-columnHeaderTitle': {fontWeight: 'bolder'},
              '.MuiDataGrid-columnHeaderTitleContainer': {borderBottom: 1 },
              '.MuiDataGrid-row': {cursor: 'pointer', borderBottom: 1},
              '.MuiDataGrid-iconButtonContainer': {visibility: 'visible' },
              '.MuiDataGrid-sortIcon': {opacity: 'inherit !important' }
              }}
              rows={children.map((c, idx) => ({ id: c.AssetID ?? idx, ...c }))}
              columns={columns}
              loading={loading}
              paginationModel={{ page: 0, pageSize: 5 }}
              pageSizeOptions={[5, 10, 25, 50]}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={onRowModesChange}
              processRowUpdate={onRowUpdate}
            />
          </div>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleCloseClick} startIcon={<FontAwesomeIcon icon={["fas","circle-x"]}/>}>Cancel</Button>
          <Tooltip title={saveTooltip} arrow>
            <span>
              <Button variant="outlined" onClick={onSaveClick} disabled={isSaveDisabled} startIcon={<FontAwesomeIcon icon={["fas","download"]}/>}>Save</Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
